import type { ImageInputRole, ModeConfig } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";
import type { AttachmentKind } from "~/shared/types/chats";

const ATTACH_KINDS = ["image", "video", "audio"] as const satisfies readonly AttachmentKind[];

export interface PerKindLimits {
    image: number | null;
    video: number | null;
    audio: number | null;
}

export interface PerKindCounts {
    image: number;
    video: number;
    audio: number;
}

export interface KindMimeTypes {
    readonly mimeTypes: readonly string[];
}

export type AttachPolicyTypes = Record<AttachmentKind, KindMimeTypes>;

type ModeLimits = Pick<ModeConfig, "maxInputImages" | "maxInputVideos" | "maxInputAudio">;

/** Per-kind лимиты режима (null — kind не ограничен / не поддержан). */
export function computeKindLimits(currentMode: ModeLimits | null): PerKindLimits {
    return {
        image: currentMode?.maxInputImages ?? null,
        video: currentMode?.maxInputVideos ?? null,
        audio: currentMode?.maxInputAudio ?? null,
    };
}

/** Сколько pending-items каждого kind уже в очереди. */
export function countByKind(items: readonly PendingUpload[]): PerKindCounts {
    return {
        image: items.filter((p) => p.kind === "image").length,
        video: items.filter((p) => p.kind === "video").length,
        audio: items.filter((p) => p.kind === "audio").length,
    };
}

/** Успешно загруженные изображения (для проверки minInputImages). */
export function countUploadedImages(items: readonly PendingUpload[]): number {
    return items.filter((item) => item.kind === "image" && item.status === "done").length;
}

/**
 * Успешно загруженные визуальные входы — картинки ЛЮБОГО канала и видео
 * (для проверки `minVisualInputs`).
 *
 * Канал здесь не важен: режим без кадров требует «хотя бы один визуальный вход»,
 * и закрыть требование может как референс, так и видео. Аудио не считается —
 * провайдер отвергает набор, в котором визуального входа нет вовсе.
 */
export function countUploadedVisualInputs(items: readonly PendingUpload[]): number {
    return items.filter((item) => (item.kind === "image" || item.kind === "video") && item.status === "done")
        .length;
}

/** Делит выбранные файлы на картинки и остальное: у каналов разные приёмники. */
export function splitImageFiles(files: readonly File[]): { images: File[]; rest: File[] } {
    return {
        images: files.filter((file) => file.type.startsWith("image/")),
        rest: files.filter((file) => !file.type.startsWith("image/")),
    };
}

export interface AttachPoolView {
    readonly limits: PerKindLimits;
    readonly counts: PerKindCounts;
    /** Сколько вложений уже занимают общие слоты (для «все слоты заняты»). */
    readonly queued: number;
}

/**
 * Чем общий приёмник (скрепка, перетаскивание, пикер тулбара) считает свои
 * лимиты и занятость.
 *
 * Когда кадрового канала у режима нет, картинки этого приёмника уходят в канал
 * референсов — значит и лимит с занятостью у них референсные, иначе интерфейс
 * мерил бы одно, а класть пришлось бы в другое.
 */
export function attachPoolView(params: {
    kindLimits: PerKindLimits;
    kindCounts: PerKindCounts;
    frameCount: number;
    referenceCount: number;
    maxReferences: number;
    imagesGoToReferences: boolean;
}): AttachPoolView {
    const { kindLimits, kindCounts, frameCount, referenceCount, maxReferences, imagesGoToReferences } =
        params;
    if (!imagesGoToReferences) {
        return { limits: kindLimits, counts: kindCounts, queued: frameCount };
    }
    return {
        limits: { ...kindLimits, image: maxReferences },
        counts: { ...kindCounts, image: referenceCount },
        queued: frameCount + referenceCount,
    };
}

export interface PickRouting {
    /** Адресат открытого пикера: роль-слот кадра или колонка референсов. */
    readonly target?: { role?: ImageInputRole; channel?: "reference" };
    /** Сколько референсов ещё принимает канал. */
    readonly referenceRoom: number;
    /** Кадрового канала у режима нет — картинки общего пикера идут в референсы. */
    readonly imagesGoToReferences: boolean;
}

/**
 * Куда лягут выбранные в пикере вложения.
 *
 * Роль-слот держит одну картинку, поэтому лишнее отбрасывается. Канал референсов
 * — пул: туда уходит выбор в пределах свободного места. Когда кадров у режима нет
 * вовсе, тот же канал забирает картинки и у общего пикера — иначе они попали бы в
 * кадры, которых режим не принимает, и уехали бы в никуда. Вложения других видов
 * (видео, аудио) канала не меняют.
 */
export function routePickedUploads(picked: readonly PendingUpload[], routing: PickRouting): PendingUpload[] {
    const { target, referenceRoom, imagesGoToReferences } = routing;
    if (target?.role !== undefined) {
        return picked.slice(0, 1).map((item) => ({ ...item, role: target.role }));
    }
    if (target?.channel !== "reference" && !imagesGoToReferences) return [...picked];

    const references = picked
        .filter((item) => item.kind === "image")
        .slice(0, referenceRoom)
        .map((item) => ({ ...item, channel: "reference" as const }));
    const rest = imagesGoToReferences ? picked.filter((item) => item.kind !== "image") : [];
    return [...references, ...rest];
}

/** kind'ы с лимитом > 0 — режим принимает такие файлы. */
export function computeAcceptedKinds(limits: PerKindLimits): AttachmentKind[] {
    return ATTACH_KINDS.filter((k) => (limits[k] ?? 0) > 0);
}

/** Суммарный лимит вложений по принимаемым kind'ам. */
export function totalAttachmentLimit(
    acceptedKinds: readonly AttachmentKind[],
    limits: PerKindLimits,
): number {
    return acceptedKinds.reduce((sum, k) => sum + (limits[k] ?? 0), 0);
}

/** Все слоты заняты: лимит задан и очередь его достигла. */
export function isAllSlotsFull(
    totalInQueue: number,
    acceptedKinds: readonly AttachmentKind[],
    limits: PerKindLimits,
): boolean {
    const limit = totalAttachmentLimit(acceptedKinds, limits);
    return limit > 0 && totalInQueue >= limit;
}

/**
 * accept для <input type="file">: точные mime из policy, иначе wildcard'ы
 * per-kind (бэк всё равно валидирует). undefined — режим не принимает файлов.
 */
export function buildFileInputAccept(
    acceptedKinds: readonly AttachmentKind[],
    policy: AttachPolicyTypes | null,
): string | undefined {
    if (acceptedKinds.length === 0) return undefined;
    if (policy) {
        const mimes: string[] = [];
        for (const k of acceptedKinds) mimes.push(...policy[k].mimeTypes);
        return mimes.join(",");
    }
    return acceptedKinds.map((k) => `${k}/*`).join(",");
}

/** Извлекает media-файлы (image/video/audio) из clipboard. Текст игнорируется. */
export function extractClipboardMediaFiles(items: DataTransferItemList | undefined): File[] {
    if (!items || items.length === 0) return [];
    const files: File[] = [];
    for (const item of Array.from(items)) {
        if (item.kind !== "file") continue;
        if (
            !item.type.startsWith("image/") &&
            !item.type.startsWith("video/") &&
            !item.type.startsWith("audio/")
        ) {
            continue;
        }
        const file = item.getAsFile();
        if (file) files.push(file);
    }
    return files;
}

/** pending-items, чей kind текущий режим больше не принимает. */
export function itemsToRemoveOnKindChange(
    items: readonly PendingUpload[],
    acceptedKindsKey: string,
): PendingUpload[] {
    const accepted = new Set(acceptedKindsKey.split(",").filter(Boolean));
    return items.filter((item) => !accepted.has(item.kind));
}
